import { useContext, useMemo } from 'react';

import {
  RIGHTS_SECRET_LOGIN_TO_ORGANIZATION_ACCOUNT,
  RIGHTS_ORGANIZATIONS,
} from '@app/constants';
import { initialAccessRights } from '@app/data';
import { useAccessRights, useMemoizeObject } from '@app/hooks';
import { useUserPrint } from '@app/hooks/useUserPrint';
import { NotificationContext } from '@app/providers';
import { useAppSelector, selectAccessRights } from '@app/store';
import { hasRightsToOrgsCrmModule } from '@app/utils';

export const useAccessToOrganizations = () => {
  const clientHost = window.location.host;

  const accessRights = useAppSelector(selectAccessRights);

  const { database } = useUserPrint();
  const { showNotification } = useContext(NotificationContext);

  const { hasUserRight: hasUserRightToSecretLinks } = useAccessRights(
    RIGHTS_ORGANIZATIONS,
    RIGHTS_SECRET_LOGIN_TO_ORGANIZATION_ACCOUNT
  );

  const memoizedAccessRights =
    useMemoizeObject(accessRights) || initialAccessRights;

  const hasRightsToOrgsCrmModuleMemo = useMemo(
    () =>
      hasRightsToOrgsCrmModule(
        clientHost,
        database,
        memoizedAccessRights,
        showNotification
      ),
    [clientHost, database, memoizedAccessRights, showNotification]
  );

  const hasUserTotalRightToSecretLinks = useMemo(
    () => hasRightsToOrgsCrmModuleMemo && hasUserRightToSecretLinks,
    [hasRightsToOrgsCrmModuleMemo, hasUserRightToSecretLinks]
  );

  const areRightsToOrgsInSettings = useMemo(() => {
    return hasRightsToOrgsCrmModule(
      clientHost,
      database,
      memoizedAccessRights,
      showNotification,
      false
    );
  }, [clientHost, database, memoizedAccessRights, showNotification]);

  return {
    hasOrganizationRestriction: hasRightsToOrgsCrmModuleMemo,
    areRightsToOrganizationsModule: hasRightsToOrgsCrmModuleMemo,
    hasUserRightToSecretLinks: hasUserTotalRightToSecretLinks,
    areRightsToOrgsInSettings,
  };
};
